"use client";

import { useMemo } from "react";
import {
  EVENTS,
  Joyride,
  STATUS,
  type EventData,
  type Placement,
  type Step,
} from "react-joyride";
import type { GuideCardArrow } from "@focal/ui-kit";
import type { UserGuideId } from "@/constants/user-guides";
import { useUserGuides } from "./useUserGuides";
import { UserGuideTooltip } from "./UserGuideTooltip";
import type { UserGuideStep } from "./types";

/**
 * The card must float on the side of the target its beak points back from:
 * a top-* beak puts the card below the target, and the beak's horizontal
 * slot maps to joyride's start/end alignment. Deriving placement from the
 * authored arrow keeps the two impossible to mismatch.
 */
const PLACEMENT_BY_ARROW: Record<GuideCardArrow, Placement> = {
  "top-left": "bottom-start",
  "top-center": "bottom",
  "top-right": "bottom-end",
  "bottom-left": "top-start",
  "bottom-center": "top",
  "bottom-right": "top-end",
};

interface UserGuideProps {
  /** Stable id persisted via PUT /api/user-guide-completion once seen. */
  guideId: UserGuideId;
  steps: UserGuideStep[];
  /** Gate to delay the guide until the page is ready (default true). */
  enabled?: boolean;
}

/**
 * One-time anchored tooltip guide (FE-1131). Wraps react-joyride v3 with
 * house defaults: no beacon, no overlay, custom Figma tooltip. The guide
 * is marked seen only when the user closes it or finishes the last step —
 * navigating away mid-tour re-shows it on the next visit (spec decision).
 */
export const UserGuide = ({ guideId, steps, enabled = true }: UserGuideProps) => {
  const { hasSeenGuide, markGuideSeen, isReady } = useUserGuides();
  const hasSeen = hasSeenGuide(guideId);

  const joyrideSteps = useMemo<Step[]>(
    () =>
      steps.map(({ arrow, ...step }) => ({
        ...step,
        placement: PLACEMENT_BY_ARROW[arrow],
        // Carried through to UserGuideTooltip, which draws the beak.
        data: { arrow },
      })),
    [steps],
  );

  const handleEvent = (data: EventData) => {
    // TARGET_NOT_FOUND ends the step; also persist then so a broken anchor
    // can't re-trigger the guide forever. (Joyride still advances the index
    // afterwards and can emit TOUR_END(FINISHED) before the cache patch
    // unmounts us — markGuideSeen ignores that second call, so DISMISSED
    // wins.)
    if (data.type === EVENTS.TARGET_NOT_FOUND) {
      markGuideSeen(guideId, "DISMISSED");
      return;
    }
    if (data.type === EVENTS.TOUR_END) {
      // The tooltip renders no primary button on the last step (Figma), so
      // a user-driven end is always X → SKIPPED: reaching the last step
      // counts as COMPLETED, an earlier close as DISMISSED. The FINISHED
      // branch stays for completeness (e.g. if a Done button is added).
      const isLastStep = data.index === data.size - 1;
      if (
        data.status === STATUS.FINISHED ||
        (data.status === STATUS.SKIPPED && isLastStep)
      ) {
        markGuideSeen(guideId, "COMPLETED");
      } else if (data.status === STATUS.SKIPPED) {
        markGuideSeen(guideId, "DISMISSED");
      }
    }
  };

  // !isReady also keeps SSR and the first hydration pass clean: nothing
  // hydrates the completions query, so it can't succeed before mount.
  if (!isReady || hasSeen || !enabled || steps.length === 0) return null;

  return (
    <Joyride
      steps={joyrideSteps}
      run
      continuous
      tooltipComponent={UserGuideTooltip}
      // Disable joyride's stock spinner — it would float unexplained over
      // the page while a missing target is polled (targetWaitTimeout).
      loaderComponent={null}
      onEvent={handleEvent}
      // The beak is drawn by the ui-kit GuideCard inside the tooltip, not
      // by joyride — hideArrow also removes the arrow's height from the
      // target offset. Flipping must stay off: the beak position comes
      // from the authored arrow, so a flipped card would point away from
      // its target.
      floatingOptions={{ hideArrow: true, flipOptions: false }}
      options={{
        // No page dimming or spotlight in the Figma mockups.
        hideOverlay: true,
        // Steps open directly as tooltips; no pulsing beacon.
        skipBeacon: true,
        // Never scroll: guide anchors live in sticky chrome (tabs bar,
        // tab footer) that is always on screen. Without this, joyride
        // scrolls the tabs bar to the top of the custom scroll container
        // on step 1 — skipBeacon puts the step straight into the tooltip
        // lifecycle, which voids the scrollToFirstStep=false exemption —
        // hiding the header and participants (FE-1131 review).
        skipScroll: true,
        // GuideCard's beak extends 20px beyond the card; 26 keeps the
        // same card-to-target distance as the old default (offset 10 +
        // arrowSize 16).
        offset: 26,
        // Wait for late-rendering targets (tab panels mount with data).
        targetWaitTimeout: 10000,
        // Above the sticky tabs bar (z-30) and page scrollbar (z-40) but
        // below dialogs (z-50): the page stays interactive mid-tour, so a
        // modal opened during the guide must paint over the tooltip
        // (joyride renders it at zIndex + 1).
        zIndex: 45,
        // v3's default close action advances to the next step; "skip" makes X end the tour (SKIPPED status → marked seen in handleEvent).
        closeButtonAction: "skip",
        // ESC also routes through the advancing close() by default —
        // disable it so the keyboard can't skip steps or fake a FINISHED.
        dismissKeyAction: false,
      }}
    />
  );
};
